import React, { createContext, useContext, useMemo } from "react";
import { getTranslation } from "@/utils";

// Collect all translation keys and defaults here
const translationLabels = {
  // Added/updated for FormLayout translation requirements
  notifySaved: ["sections.notify.saved", "Changes saved successfully"],
  notifyErrorSaving: [
    "sections.notify.error-saving",
    "Something went wrong. Please try again",
  ],

  pleaseFillSectionName: [
    "sections.error.please-fill-section-name",
    "A section requires a name",
  ],
  availableFields: ["settings.edit-form.available-fields", "Available Fields"],
  addSection: ["settings.edit-form.add-section", "Add Section"],
  sectionMustHaveAtLeastOneField: [
    "sections.error.section-must-have-at-least-one-field",
    "A section requires at least one field",
  ],
  pleaseFillSectionLabel: [
    "sections.error.section-name-is-required",
    "Please fill section label",
  ],
  descriptionLabel: ["sections.labels.description", "Description Info"],
  noFields: ["sections.labels.no-fields", "Drag and drop fields here"],
  valueScoreLabel: ["value-score.title", "Value Score"],
  valueScoreTooltip: [
    "sections.labels.value-score-tooltip",
    "Value Score is enabled through General Settings. To remove it, disable it in General Settings.",
  ],
  mandatoryFieldRemoval: [
    "sections.notify.confirm-mandatory-field-removal",
    "Are you sure you want to remove this required field?",
  ],

  backToFieldSettings: [
    "settings.edit-form.back-button",
    "Back to Field Settings",
  ],
  projectSettings: ["settings.edit-form.project-settings", "Project Settings"],
  selectEntity: ["generic.list.placeholder", "Select entity"],
  removeField: ["sections.labels.remove-field", "Remove Field"],
  searchField: ["generic.fields.search", "Search Field"],
  customFields: ["sections.labels.custom-fields", "Custom Fields"],
  systemFields: ["sections.labels.system-fields", "System Fields"],

  newSectionPlaceholder: [
    "sections.labels.section-name-placeholder",
    "Type Section Name",
  ],
  confirmCancel: [
    "sections.notify.confirm-cancel",
    "Are you sure you want to cancel? All unsaved changes will be lost.",
  ],
};

type FormLayoutLabels = { [K in keyof typeof translationLabels]: string };

const TranslationContext = createContext<FormLayoutLabels | undefined>(
  undefined,
);

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Compute all translations once
  const labels = useMemo(() => {
    const result: any = {};
    for (const [key, [translationKey, defaultValue]] of Object.entries(
      translationLabels,
    )) {
      result[key] = getTranslation(translationKey, defaultValue);
    }
    return result as FormLayoutLabels;
  }, []);

  return (
    <TranslationContext.Provider value={labels}>
      {children}
    </TranslationContext.Provider>
  );
};

export function useFormLayoutLabels() {
  const ctx = useContext(TranslationContext);
  if (!ctx) {
    throw new Error(
      "useFormLayoutLabels must be used within a TranslationProvider",
    );
  }
  return ctx;
}
